import {changeIsUserSupport} from './../ducks/userEntry';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult} from 'Core/models/Async';
import {createValidatedEmail} from 'Core/models/Email';
import {createValidatableEmptyPhone, createValidatedPhone} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import {createValidatedNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import * as E from 'fp-ts/lib/Either';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {List} from 'immutable';
import {all, call, put, takeLatest, select} from 'redux-saga/effects';
import agentNumberApi from 'Users/api/agentNumberApi';
import {AgentNumberWithStatusDto} from 'Users/api/api';
import userApi from 'Users/api/userApi';
import {AvailableAgentNumber} from 'Users/components/UserAdministration/UserEntry/AgentNumberInput';
import {loadAgentSupportUserType} from 'Users/ducks/agentSupportUserType';
import {loadAgentUserType} from 'Users/ducks/agentUserType';
import * as FSL from 'Users/ducks/fieldSalesLeaderUserType';
import {loadFieldSalesLeaders} from 'Users/ducks/fieldVicePresidentUserType';
import {
  addAccessFromLineOfBusiness,
  changeAccountType,
  changeAgentRole,
  changeBeneficiariesRole,
  changeBosToolsRole,
  changeEmail,
  changeEnrollTrackRole,
  changeFirstName,
  changeLastName,
  changePhoneNumber,
  changeUserName,
  getUserDetail,
  UserEntryActionType,
  changeMessagingRole,
  changeAppEntryRole,
} from 'Users/ducks/userEntry';
import {changeHasAHLAccess, changeHasNewYorkAccess, changeIsExternalAdmin} from 'Users/ducks/vipUserType';
import {AgentClaims} from 'Users/models/AgentClaims';
import {
  BeneficiaryClaims,
  BOSToolsClaims,
  EnrollTrackClaims,
  MessagingClaims,
  UserDetailsModel,
  UserTypeModel,
  UserTypeModelKey,
  ApplicationEntryClaims,
} from 'Users/models/Claims';
import {LineOfBusiness} from 'Users/models/LineOfBusiness';
import {ValidAgencyAssociation} from 'Users/models/User/Agent/AgencyAssociation';
import {ValidAgentSupportUserType} from 'Users/models/User/Agent/AgentSupportUserType';
import * as AgentUserTypeValue from 'Users/models/User/Agent/AgentUserType';
import {
  SupportAgencyAssociation,
  ValidSupportAgencyAssociation,
} from 'Users/models/User/Agent/SupportAgencyAssociation';
import {ExistingTaxIdentificationNumber} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {AgentRole} from 'Users/models/User/AgentUserType';
import {BeneficiariesRole} from 'Users/models/User/BeneficiariesUserType';
import {BosToolsRole} from 'Users/models/User/BosToolsUserType';
import {EnrollTrackRole} from 'Users/models/User/EnrollmentTrackingUserType';
import {MessagingRole} from 'Users/models/User/MessagingUserType';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import {UserRouteConstants} from 'Users/Routes';
import {ReducerState} from 'rootReducer';
import {changeUser} from 'Users/ducks/userEntryEdit';
import {AppEntryRole} from 'Users/models/User/AppEntry/KeyerUser';
import {UserSupportUserType} from 'Users/models/User/EnrollTrack/UserSupportUserType';

export function* loadAgentSupport(agentUserType: AgentClaims) {
  yield put(addAccessFromLineOfBusiness(LineOfBusiness.Agent));
  yield put(changeAgentRole(AgentRole.AgentSupport));

  let agencyAssociations = List<SupportAgencyAssociation>();

  for (const agencyAssociation of agentUserType.agencyAssociations) {
    const supportAgentRoleIds = agencyAssociation.roles.map(role => role.id);
    const agentNumbers: AsyncResult<AgentNumberWithStatusDto[]> = yield call(
      agentNumberApi.getAgentNumbersByTaxIdentifier,
      agencyAssociation.maskedIdentifier,
      agencyAssociation.encryptedIdentifier
    );

    if (E.isRight(agentNumbers)) {
      agencyAssociations = agencyAssociations.push(
        ValidSupportAgencyAssociation.create(
          new ExistingTaxIdentificationNumber(
            agencyAssociation.maskedIdentifier,
            agencyAssociation.encryptedIdentifier,
            agentNumbers.right.map(
              ({value, isNewYork, status}): AvailableAgentNumber => ({
                value: value!,
                isNewYork: isNewYork!,
                status: {id: status!.id!, name: status!.name!},
              })
            )
          ),
          {
            canViewAccounts: supportAgentRoleIds.includes(RoleId.AgencySupportWithAccounts),
            canViewClaims: supportAgentRoleIds.includes(RoleId.AgencySupportWithClaims),
            canViewCommissionsAnd1099s: supportAgentRoleIds.includes(RoleId.AgencySupportWithCommissionsAnd1099s),
            canViewCorrespondence: supportAgentRoleIds.includes(RoleId.AgencySupportWithCorrespondence),
            canViewPolicies: supportAgentRoleIds.includes(RoleId.AgencySupportWithPolicies),
            canViewPolicyServiceRequest: supportAgentRoleIds.includes(RoleId.AgencySupportWithPolicyServiceRequest),
            canViewPendingUnderwritingRequirements: supportAgentRoleIds.includes(
              RoleId.AgencySupportWithUnderwritingRequirements
            ),
          },
          agencyAssociation.agentNumbers
        )
      );
    }
  }

  yield put(
    loadAgentSupportUserType(ValidAgentSupportUserType.create(agencyAssociations) as ValidAgentSupportUserType)
  );
}

export function* loadAgent(agentUserType: AgentClaims) {
  yield put(addAccessFromLineOfBusiness(LineOfBusiness.Agent));
  yield put(changeAgentRole(AgentRole.Agent));

  let agencyAssociations = List();

  for (const agencyAssociation of agentUserType.agencyAssociations) {
    const agentNumbers: AsyncResult<AgentNumberWithStatusDto[]> = yield call(
      agentNumberApi.getAgentNumbersByTaxIdentifier,
      agencyAssociation.maskedIdentifier,
      agencyAssociation.encryptedIdentifier
    );

    if (E.isRight(agentNumbers)) {
      agencyAssociations = agencyAssociations.push(
        ValidAgencyAssociation.create(
          new ExistingTaxIdentificationNumber(
            agencyAssociation.maskedIdentifier,
            agencyAssociation.encryptedIdentifier,
            agentNumbers.right.map(
              ({value, isNewYork, status}): AvailableAgentNumber => ({
                value: value!,
                isNewYork: isNewYork!,
                status: {id: status!.id!, name: status!.name!},
              })
            )
          ),
          agencyAssociation.roles?.map(r => r.id)?.includes(RoleId.AgencyAdministrator),
          agencyAssociation.agentNumbers
        )
      );
    }
  }

  yield put(
    loadAgentUserType(
      AgentUserTypeValue.ValidAgentUserType.create(agencyAssociations) as AgentUserTypeValue.ValidAgentUserType
    )
  );
}

export function* loadFieldVicePresident(agentUserType: AgentClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.Agent)),
    put(changeAgentRole(AgentRole.FieldVicePresident)),
    put(loadFieldSalesLeaders(agentUserType.fieldSalesLeaders)),
  ]);
}

export function* loadFieldSalesLeader(agentUserType: AgentClaims) {
  const {value: recruiterCodeValue, isAhl, isNewYork} = agentUserType.recruiterCode;

  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.Agent)),
    put(changeAgentRole(AgentRole.FieldSalesLeader)),
    put(FSL.changeFieldSalesLeaderAHL(agentUserType.isAhl)),
    put(FSL.changeFieldSalesLeaderNewYork(agentUserType.isNewYork)),
    put(FSL.changeFieldSalesLeaderRecruiterCode(recruiterCodeValue, isAhl, isNewYork)),
  ]);
}

const getRoleFromAgentUserType = (agentUserType: AgentClaims) =>
  pipe(
    agentUserType.agencyAssociations,
    O.fromNullable,
    O.chain(NEA.fromArray),
    O.map(NEA.head),
    O.map(association => association.roles.map(role => role.id)),
    O.getOrElse(() => agentUserType.roles.map(role => role.id)),
    NEA.fromArray,
    O.map(NEA.head)
  );

const agentSupportRoleIds = [
  RoleId.AgencySupportWithAccounts,
  RoleId.AgencySupportWithClaims,
  RoleId.AgencySupportWithCommissionsAnd1099s,
  RoleId.AgencySupportWithCorrespondence,
  RoleId.AgencySupportWithPolicies,
  RoleId.AgencySupportWithPolicyServiceRequest,
  RoleId.AgencySupportWithUnderwritingRequirements,
];

export function* loadAgentUser(agentUserType: AgentClaims): unknown {
  const roleIdOption = getRoleFromAgentUserType(agentUserType);

  if (O.isSome(roleIdOption)) {
    const roleId = roleIdOption.value;

    if (roleId === RoleId.Producer || roleId === RoleId.AgencyAdministrator) {
      yield loadAgent(agentUserType);
    } else if (roleId === RoleId.VIP || roleId === RoleId.AllstateExternalAdministrator) {
      yield all([
        put(addAccessFromLineOfBusiness(LineOfBusiness.Agent)),
        put(changeAgentRole(AgentRole.VIP)),
        put(changeHasAHLAccess(agentUserType.isAhl)),
        put(changeHasNewYorkAccess(agentUserType.isNewYork)),
        put(changeIsExternalAdmin(agentUserType.roles.map(r => r.id).includes(RoleId.AllstateExternalAdministrator))),
      ]);
    } else if (roleId === RoleId.FieldVicePresident) {
      yield loadFieldVicePresident(agentUserType);
    } else if (roleId === RoleId.FieldSalesLeader) {
      yield loadFieldSalesLeader(agentUserType);
    } else if (agentSupportRoleIds.includes(roleId)) {
      yield loadAgentSupport(agentUserType);
    }
  } else {
    yield put(createSnackbar('Unable to load user.'));
    yield put(push(UserRouteConstants.USERS));
  }
}

export function* loadBeneficiaryUserType(beneficiaryUserType: BeneficiaryClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.Beneficiary)),
    put(changeBeneficiariesRole(beneficiaryUserType.roles[0].id as BeneficiariesRole)),
  ]);
}

export function* loadEnrollTrackUserType(enrollTrackUserType: EnrollTrackClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.EnrollmentTracking)),
    put(changeEnrollTrackRole(enrollTrackUserType.roles[0].id as EnrollTrackRole)),
  ]);
}

export function* loadBusinessOperationsSupportUserType(businessOperationsSupportUserType: BOSToolsClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.BOSTools)),
    put(changeBosToolsRole(businessOperationsSupportUserType.roles[0].id as BosToolsRole)),
  ]);
}

export function* loadApplicationEntryUserType(applicationEntryUserType: ApplicationEntryClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.AppEntry)),
    put(changeAppEntryRole(applicationEntryUserType.roles[0].id as AppEntryRole)),
  ]);
}

export function* loadMessagingUserType(messagingUserType: MessagingClaims) {
  yield all([
    put(addAccessFromLineOfBusiness(LineOfBusiness.Messaging)),
    put(changeMessagingRole(messagingUserType.roles[0].id as MessagingRole)),
  ]);
}

export function* getUserDetailSaga() {
  function* getUserDetailApi(action: ReturnType<typeof getUserDetail>) {
    const result: AsyncResult<UserDetailsModel> = yield call(userApi.getUser, action.payload.userId);

    if (E.isRight(result)) {
      yield put(changeFirstName(createValidatedNonEmptyString(result.right.firstName)));
      yield put(changeLastName(createValidatedNonEmptyString(result.right.lastName)));
      yield put(changeEmail(createValidatedEmail(result.right.email)));
      yield put(changeUserName(createValidatedNonEmptyString(result.right.userName)));
      yield put(
        changePhoneNumber(
          result.right.mobilePhone ? createValidatedPhone(result.right.mobilePhone) : createValidatableEmptyPhone()
        )
      );
      yield put(
        changeAccountType(result.right.exclusivityStatus?.id === ExclusivityStatus.Internal ? 'Internal' : 'External')
      );
      const enrollTrackUserType = result.right.userTypes.find(x => x.$type === UserTypeModelKey.EnrollTrack);
      if (enrollTrackUserType) {
        const isUserSupport = (enrollTrackUserType as EnrollTrackClaims).roles.some(
          x => x.id === new UserSupportUserType().enrollTrackRole
        );
        yield put(changeIsUserSupport(isUserSupport));
      }
      const doNothing = function*() {};

      yield all(
        result.right.userTypes.map(ut =>
          UserTypeModel.match({
            [UserTypeModelKey.AgentSite]: loadAgentUser,
            [UserTypeModelKey.Beneficiary]: loadBeneficiaryUserType,
            [UserTypeModelKey.EnrollTrack]: loadEnrollTrackUserType,
            [UserTypeModelKey.BosTools]: loadBusinessOperationsSupportUserType,
            [UserTypeModelKey.Messaging]: loadMessagingUserType,
            [UserTypeModelKey.ApplicationEntry]: loadApplicationEntryUserType,
            Empty: () => doNothing,
          })(ut)
        )
      );

      const user = yield select((state: ReducerState) => state.user.userEntry);
      yield put(changeUser(user));
    } else {
      yield all([put(createSnackbar('Unable to load user')), put(push(UserRouteConstants.USERS))]);
    }
  }

  yield takeLatest(UserEntryActionType.GET_USER_DETAIL, getUserDetailApi);
}

export default getUserDetailSaga;
